import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import type { WebSocketMessage } from "@shared/websocket-types";

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    try {
      // Get the WebSocket URL based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          
          // Invalidate relevant queries based on message type
          switch (data.type) {
            case 'performer_created':
            case 'performer_updated':
            case 'performer_deleted':
            case 'performers':
            case 'raffle_winner':
            case 'raffle_reset':
              // Refresh performers for the specific jam night
              if ('jamNightId' in data && data.jamNightId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/performers?jamNightId=${data.jamNightId}`]
                });
              }
              // Also refresh general performers query
              queryClient.invalidateQueries({
                queryKey: ['/api/performers']
              });
              // Refresh jam nights list to update performer counts
              queryClient.invalidateQueries({
                queryKey: ["jamNights"]
              });
              break;
              
            case 'participant_created':
            case 'participant_updated':
              // Refresh participants for the specific organisation
              if ('organisationId' in data && data.organisationId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/participants?organisationId=${data.organisationId}`]
                });
              }
              
              // For participant updates, also refresh all performer queries
              // since performers display participant data (name, email, phone, instagram)
              if (data.type === 'participant_updated') {
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/performers');
                  }
                });
              }
              break;
              
            case 'message_created':
              // Refresh messages for the specific jam night
              if ('jamNightId' in data && data.jamNightId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/messages?jamNightId=${data.jamNightId}`]
                });
                // Also refresh event feed to include the new message
                queryClient.invalidateQueries({
                  queryKey: [`/api/events/${data.jamNightId}/feed`]
                });
              }
              break;

            case 'event_feed':
              // Refresh event feed for the specific event
              if ('eventId' in data && data.eventId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/events/${data.eventId}/feed`]
                });
              }
              break;

            case 'event_updated':
            case 'event_created':
            case 'event_deleted':
            case 'jam_night_updated':
            case 'jam_night_created':
            case 'jam_night_deleted':
              // Refresh events list for home page
              queryClient.invalidateQueries({
                queryKey: ["events"]
              });
              // Also invalidate the legacy jamNights key in case it's used somewhere
              queryClient.invalidateQueries({
                queryKey: ["jamNights"]
              });
              // Refresh specific event details - check both eventId and jamNightId
              const eventId = ('eventId' in data ? data.eventId : null) || ('jamNightId' in data ? data.jamNightId : null);
              if (eventId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/events/${eventId}`]
                });
                // Also invalidate queries that might include this event data
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && 
                           (queryKey.includes(`/api/events/${eventId}`) ||
                            queryKey.includes(`jamNightId=${eventId}`) ||
                            queryKey.includes(`eventId=${eventId}`));
                  }
                });
              }
              break;

            case 'theme_updated':
              // Only handle theme updates for the current user
              if ('userId' in data && data.userId === user?.id && 'theme' in data && data.theme) {
                // Update the user data in localStorage
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                    const userData = JSON.parse(storedUser);
                    userData.theme = data.theme;
                    localStorage.setItem('user', JSON.stringify(userData));
                  } catch (error) {
                    // Silent fail - error updating theme
                  }
                }
                
                // Force page reload to apply theme change immediately
                window.location.reload();
              }
              break;

            case 'user_settings_updated':
              if ('userId' in data && data.userId === user?.id && 'settings' in data && data.settings) {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                    const userData = JSON.parse(storedUser);
                    const updatedUser = { ...userData, ...data.settings };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    window.dispatchEvent(new Event('user-settings-changed'));
                  } catch (error) {
                    // Silent fail - error updating settings
                  }
                }
              }
              break;
              
            case 'force_logout':
              // Check if this force logout is for the current user
              if ('userId' in data && data.userId === user?.id) {
                
                // Clear local storage
                localStorage.removeItem('user');
                
                // Close WebSocket connection cleanly
                if (wsRef.current) {
                  wsRef.current.close(1000, 'Force logout');
                }
                
                // Redirect to login page
                window.location.href = '/login';
              }
              break;
              
            case 'act:created':
            case 'act:updated':
            case 'act:deleted':
              // Refresh acts for the specific organisation
              if (data.organisationId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts?organisationId=${data.organisationId}`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts`]
                });
                // Refresh specific act if actId is provided
                if (('actId' in data && data.actId) || ('act' in data && data.act?.id)) {
                  const actId = (('actId' in data && data.actId) || ('act' in data && data.act?.id)) as number;
                  queryClient.invalidateQueries({
                    queryKey: [`/api/acts/${actId}`]
                  });
                }
              }
              break;

            case 'external-event:created':
            case 'external-event:updated':
            case 'external-event:deleted':
              // Refresh external events for the specific organisation
              if (data.organisationId) {
                // Invalidate all external events queries (with and without org ID)
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events?organisationId=${data.organisationId}`]
                });
                // Also invalidate using predicate to catch all variations
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/external-events');
                  }
                });
                // Refresh specific event if eventId is provided
                if (('eventId' in data && data.eventId) || ('event' in data && data.event?.id)) {
                  const eventId = (('eventId' in data && data.eventId) || ('event' in data && data.event?.id)) as number;
                  queryClient.invalidateQueries({
                    queryKey: [`/api/external-events/${eventId}`]
                  });
                }
              }
              break;

            case 'booking:created':
            case 'booking:updated':
            case 'booking:cancelled':
              
              // Refresh bookings for the specific organisation
              if (data.organisationId) {
                // Invalidating booking queries for organization
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/bookings');
                  }
                });
                // Also refresh acts and events since bookings affect their statistics
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events`]
                });
              }
              
              // Refresh specific booking if bookingId is provided
              if (('bookingId' in data && data.bookingId) || ('booking' in data && data.booking?.id)) {
                const bookingId = (('bookingId' in data && data.bookingId) || ('booking' in data && data.booking?.id)) as number;
                // Invalidating specific booking
                queryClient.invalidateQueries({
                  queryKey: [`/api/bookings/${bookingId}`]
                });
                // Force a refetch to ensure the data updates
                queryClient.refetchQueries({
                  queryKey: [`/api/bookings/${bookingId}`]
                });
              }
              break;

            case 'organization_membership_changed':
              // Organization membership changed - refresh organization and user data
              // Organization membership changed - refreshing data
              queryClient.invalidateQueries({ queryKey: ["organisations"] });
              queryClient.invalidateQueries({ queryKey: ["users"] });
              
              // If this affects the current user, refresh their auth data
              if (data.userId === user?.id) {
                // Current user organization membership changed - refreshing auth data
                
                // Check if user was removed from organization and redirect if needed
                const isRemoved = data.action === 'removed';
                const removedOrgId = data.organizationId;
                
                // Fetch fresh user data from server
                fetch('/api/auth/user', {
                  method: 'GET',
                  credentials: 'include',
                })
                .then(response => {
                  if (response.ok) {
                    return response.json();
                  }
                  throw new Error('Failed to fetch user data');
                })
                .then(userData => {
                  // Update localStorage with fresh user data
                  localStorage.setItem('user', JSON.stringify(userData));
                  
                  // Trigger a custom event to update the auth context
                  window.dispatchEvent(new CustomEvent('user-auth-changed', { detail: userData }));
                  
                  // If user was removed from organization, check if they need to be redirected
                  if (isRemoved && removedOrgId) {
                    const currentPath = window.location.pathname;
                    // User removed from organization - check if redirect needed
                    
                    // Check if current page is an event from the organization they were removed from
                    const eventMatch = currentPath.match(/\/event\/(\d+)/);
                    // Check if current page is an event page
                    if (eventMatch) {
                      const eventId = parseInt(eventMatch[1]);
                      // Checking event against removed organization
                      
                      // Fetch the event to check its organization
                      fetch(`/api/events/${eventId}`, {
                        method: 'GET',
                        credentials: 'include',
                      })
                      .then(response => {
                        if (response.ok) {
                          return response.json();
                        }
                        // If we can't fetch the event, assume user no longer has access
                        throw new Error('Event not accessible');
                      })
                      .then(event => {
                        // Retrieved event data
                        // Check if this event belongs to the organization they were removed from
                        if (event && event.organisationId === removedOrgId) {
                          // Event belongs to removed organization - redirecting
                          window.location.href = '/';
                        } else {
                          // Event does not belong to removed organization
                        }
                      })
                      .catch(error => {
                        // Event not accessible after organization removal - redirecting
                        window.location.href = '/';
                      });
                      return; // Exit early to avoid checking other page types
                    }
                    
                    // Check if current page is an analytics page from the organization they were removed from
                    const analyticsMatch = currentPath.match(/\/analytics\/(\d+)/);
                    // Check if current page is analytics page
                    if (analyticsMatch) {
                      const analyticsOrgId = parseInt(analyticsMatch[1]);
                      // Checking analytics against removed organization
                      
                      // Check if this analytics page belongs to the organization they were removed from
                      if (analyticsOrgId === removedOrgId) {
                        // Analytics page belongs to removed organization - redirecting
                        window.location.href = '/';
                      } else {
                        // Analytics page does not belong to removed organization
                      }
                      return; // Exit early to avoid checking other page types
                    }
                    
                    // Check if current page is a participant detail from the organization they were removed from
                    const participantMatch = currentPath.match(/^\/participants\/(\d+)/);
                    if (participantMatch) {
                      // Since there's no single participant endpoint, we'll check if user still has access
                      // to any organization. If they were removed from their last organization and are viewing
                      // a participant page, redirect them to home
                      const userOrganizations = userData.organisationUsers || [];
                      const userHasOrganizations = userOrganizations.length > 0;
                      
                      if (!userHasOrganizations) {
                        // User has no organizations - redirecting
                        window.location.href = '/';
                      }
                    }
                  }
                  
                  // Also invalidate home page data to refresh organization groups
                  queryClient.invalidateQueries({ queryKey: ["events"] });
                  queryClient.invalidateQueries({ queryKey: ["jamNights"] });
                })
                .catch(error => {
                  // Error refreshing user data
                });
              }
              break;
          }
        } catch (error) {
          // Error parsing WebSocket message
        }
      };

      wsRef.current.onclose = (event) => {
        // WebSocket disconnected
        // Only attempt reconnection if it wasn't a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            // Attempting WebSocket reconnection
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        // WebSocket error - silent fail
      };

    } catch (error) {
      // Failed to create WebSocket connection
    }
  }, [queryClient, user?.id]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnect: connect
  };
}